// Display for the 'proposal_summary_panel' business module (display-only — no capture form,
// same shape/role as EstimateSummaryDisplayCard/ApprovedEstimateSnapshotCard in WorkflowCard.tsx).
// Bound to every proposal status (proposal_prepared/returned/approved/sent/accepted — see
// api-service app/db/seed/sc/workflow_definition_seed.py MODULE_DISPLAYS).
//
// Migrated to the section-based schema (proposal structure and schema.md): the proposal is now a
// real, section-instance document (`ProposalOut.sections[]`) rather than one flat estimate-lines
// preview + three ad hoc draft fields. This card renders every section in order, lets a Sales
// Agent toggle which are `included` while the proposal is still draft-editable (PATCH .../
// sections/{id}), and — for the PRICING_OPTIONS section — swap which snapshotted option is
// recommended.
//
// Phase C: every section whose content is a genuine user SELECTION (not purely computed/derived)
// gets a real "Select"/"Edit" popup — matching the approved mockup's own click-to-open-modal
// pattern (per user instruction: popups in this app should appear the way they do in the mockup,
// not as inline-expanding panels) — see ProposalSectionEditors.tsx. Drag-reorder of sections and
// the Settings-side template/master-data admin screens remain a later Phase C follow-up.
//
// The proposal document itself is created/advanced/locked by workflow module_action hooks
// (proposals.prepareOrAdvance / proposals.lockOnApproval) and approved/returned by the generic
// workflow-transition buttons (WorkflowCard's own transitions list) — this card never touches
// either of those. "Download PDF" is real (app.domain.proposals.pdf, reportlab) — renders the
// exact same section instances this preview shows.
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useChangeProposalTemplateMutation, useGetCurrentProposalQuery, useLazyGetProposalPdfQuery,
  useListProposalTemplatesQuery, useSetOptionIncludedMutation, useSetRecommendedOptionMutation,
  useUpdateProposalSectionMutation, type EstimateOptionRef, type ProposalOut, type ProposalSection,
} from '@/redux/internal-services/proposalsApi';
import {
  useListEstimatesForEngagementQuery,
  useGetEstimateVersionQuery,
} from '@/redux/internal-services/estimatesApi';
import { apiErrorMessage } from '@/utils/apiError';
import type { ProposalSurfaceInteractPayload } from '@/components/SAM/samActionTypes';
import {
  PhotoPickerModal, ExclusionsPickerModal, TermClausesPickerModal, PreviousWorkPickerModal,
  PaymentTermPickerModal, WarrantyEditorModal, type SelectedPhoto, type WarrantyEntry,
} from '@/components/Proposals/ProposalSectionEditors';
import { DEFAULT_BRAND } from '@/theme/brandDefaults';

interface ProposalDisplayCardProps {
  engagementId: string;
  /** Used by the PHOTOS section's picker modal to list the property's own media library. */
  propertyId?: string;
  /** Opens the AssessmentCaptureCard modal — wired from WorkflowCard via the same hook EstimateSummaryDisplayCard uses. */
  onOpenAssessment?: () => void;
  /** Expands the "Estimate behind this proposal" section in EstimateSummaryDisplayCard — passed from WorkflowCard. */
  onViewEstimate?: () => void;
  /** Bumped by SAM's proposal_prepared_open tool with view="preview_proposal" — auto-opens the proposal preview modal. */
  previewOpenSignal?: number;
  /** SAM's proposal_surface_interact tool, surface='preview_proposal' — scroll/close the
   *  already-open preview modal. */
  interactSignal?: ProposalSurfaceInteractPayload & { tick: number } | null;
  /** Reports whether the preview modal is open on every change, whoever caused it — SAM or a
   *  manual click — so the realtime session's screen-awareness push stays accurate. */
  onSurfaceContext?: (state: { preview_open: boolean }) => void;
}

function fmtMoney(amount: number | null | undefined, currency = 'USD') {
  if (amount == null) return '—';
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount); }
  catch { return `$${amount.toFixed(0)}`; }
}
function humanize(s: string) {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
function fmtDateTime(iso: string) {
  try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return iso; }
}

const PROPOSAL_STATUS_META: Record<string, { key: string; defaultLabel: string; cls: string }> = {
  DRAFT: { key: 'workflow.proposalStatusPrepared', defaultLabel: 'Draft', cls: 'bg-warning/15 text-warning-text' },
  APPROVED: { key: 'workflow.proposalStatusApproved', defaultLabel: 'Approved', cls: 'bg-success/15 text-success-text' },
  REVISION_REQUESTED: { key: 'workflow.proposalStatusReturned', defaultLabel: 'Revision requested', cls: 'bg-danger/15 text-danger-text' },
  SENT: { key: 'workflow.proposalStatusSent', defaultLabel: 'Sent to client', cls: 'bg-accent/15 text-accent-text' },
  ACCEPTED: { key: 'workflow.proposalStatusAccepted', defaultLabel: 'Accepted', cls: 'bg-success/15 text-success-text' },
  REJECTED: { key: 'workflow.proposalStatusRejected', defaultLabel: 'Rejected', cls: 'bg-danger/15 text-danger-text' },
};

type PricingLine = { pricing_line_code: string; label: string; presentation_amount: number };
type PricingOption = {
  option_id: string; option_role: 'PRIMARY' | 'ALTERNATE'; display_label: string;
  pricing_lines: PricingLine[]; subtotal: number;
  discount: { show_separately: boolean; amount: number };
  tax: { taxable_amount: number; tax_amount: number };
  total: number;
};

/** Recomputes subtotal/taxable/tax/total for one option after a pricing_line amount is edited
 *  by hand — mirrors app.domain.proposals.pricing_presentation.build_option_pricing's own math
 *  (subtotal = sum(lines); taxable = subtotal - discount; total = taxable + tax), so the card
 *  never shows a total that disagrees with its own line items. The tax RATE isn't stored on the
 *  option (only taxable_amount/tax_amount are) — it's derived from the option's own numbers
 *  rather than re-asking the backend, so an option with no tax stays untaxed after the edit. */
function recomputeOption(opt: PricingOption, lineCode: string, newAmount: number): PricingOption {
  const pricing_lines = opt.pricing_lines.map((l) => (l.pricing_line_code === lineCode ? { ...l, presentation_amount: newAmount } : l));
  const subtotal = Math.round(pricing_lines.reduce((sum, l) => sum + l.presentation_amount, 0) * 100) / 100;
  const taxable_amount = Math.round((subtotal - opt.discount.amount) * 100) / 100;
  const priorTaxRate = opt.tax.taxable_amount > 0 ? opt.tax.tax_amount / opt.tax.taxable_amount : 0;
  const tax_amount = Math.round(taxable_amount * priorTaxRate * 100) / 100;
  const total = Math.round((taxable_amount + tax_amount) * 100) / 100;
  return { ...opt, pricing_lines, subtotal, tax: { ...opt.tax, taxable_amount, tax_amount }, total };
}

function PricingOptionsBody({
  section, currency, isDraftEditable, engagementId, allOptions,
}: {
  section: ProposalSection; currency: string; isDraftEditable: boolean; engagementId: string;
  /** Every option snapshotted onto the proposal, INCLUDING the excluded ones — the section's own
   *  structured_data only carries what the client sees, so the excluded ones would otherwise be
   *  invisible here and impossible to put back. */
  allOptions: EstimateOptionRef[];
}) {
  const { t } = useTranslation();
  const data = (section.content.structured_data ?? {}) as { options?: PricingOption[]; presentation_mode?: string };
  const options = data.options ?? [];
  const [setRecommended, { isLoading }] = useSetRecommendedOptionMutation();
  const [setIncluded, { isLoading: changingInclusion }] = useSetOptionIncludedMutation();
  const [updateSection, { isLoading: savingLine }] = useUpdateProposalSectionMutation();
  const [error, setError] = useState('');
  // Composite `${option_id}:${pricing_line_code}` key — the only in-place-editable amount is a
  // line's own presentation_amount, not the computed subtotal/total below it.
  const [editingLineKey, setEditingLineKey] = useState<string | null>(null);
  const [lineDraft, setLineDraft] = useState('');

  const busy = isLoading || changingInclusion || savingLine;
  const canEditAllowed = isDraftEditable && section.allow_user_edit;

  const saveLineAmount = async (optionId: string, lineCode: string, newAmount: number) => {
    setError('');
    const updatedOptions = options.map((o) => (o.option_id === optionId ? recomputeOption(o, lineCode, newAmount) : o));
    try {
      await updateSection({
        engagementId, proposalSectionId: section.proposal_section_id,
        structured_data: { ...data, options: updatedOptions },
      }).unwrap();
    } catch (e) {
      setError(apiErrorMessage(e, t, t('workflow.proposalPricingLineSaveFailed', 'Could not save that amount.')));
    }
  };
  const excluded = allOptions.filter((o) => !o.included);
  const recommendedId = allOptions[0]?.option_id;

  const toggleIncluded = async (optionId: string, included: boolean) => {
    setError('');
    try {
      await setIncluded({ engagementId, option_id: optionId, included }).unwrap();
    } catch (e) {
      setError(apiErrorMessage(e, t, t('workflow.proposalOptionToggleFailed', 'Could not change which options are included.')));
    }
  };

  const recommended = options.find((o) => o.option_role === 'PRIMARY') ?? options[0];
  const modeLabel: Record<string, string> = {
    SINGLE_TOTAL: t('workflow.proposalPricingSingleTotal', 'Single total'),
    COST_TYPE_SUMMARY: t('workflow.proposalPricingByCostType', 'By cost type'),
    CUSTOMER_CATEGORY_SUMMARY: t('workflow.proposalPricingByCategory', 'By category'),
  };
  const mode = (section.content.structured_data as { presentation_mode?: string } | null)?.presentation_mode;

  if (options.length === 0) {
    return <p className="text-xs italic text-ui-text3">{t('workflow.proposalNoPricingYet', 'No estimate option has been carried into this proposal yet.')}</p>;
  }
  return (
    <div className="space-y-3">
      {mode && modeLabel[mode] && (
        <p className="text-xs text-ui-text3">
          {t('workflow.proposalPricingPresentation', 'Pricing presentation')}: {modeLabel[mode]}
        </p>
      )}
      {options.map((opt) => (
        <div key={opt.option_id} className={`rounded-lg border px-2.5 py-2 ${opt.option_role === 'PRIMARY' ? 'border-accent bg-accent-soft/40' : 'border-ui-border'}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="flex min-w-0 items-center gap-2 text-xs font-semibold text-ui-text">
              {/* The recommended option has no checkbox: it is always on the proposal, and a
                  disabled tick that never moves reads as broken. Recommend another option first
                  and this one becomes removable. */}
              {isDraftEditable && opt.option_id !== recommendedId && (
                <input
                  type="checkbox"
                  checked
                  disabled={busy}
                  aria-label={t('workflow.proposalOptionExclude', 'Remove this option from the proposal')}
                  onChange={() => void toggleIncluded(opt.option_id, false)}
                  className="flex-shrink-0 accent-accent-fill"
                />
              )}
              {opt.display_label}
              {opt.option_role === 'PRIMARY' && (
                <span className="ml-1.5 rounded-full bg-accent-fill px-1.5 py-0.5 text-xs font-medium text-on-accent">
                  {t('workflow.proposalRecommended', 'Recommended')}
                </span>
              )}
            </span>
            <span className="text-xs font-semibold text-ui-text">{fmtMoney(opt.total, currency)}</span>
          </div>
          <div className="mt-1 space-y-0.5">
            {opt.pricing_lines.map((l) => {
              const lineKey = `${opt.option_id}:${l.pricing_line_code}`;
              const isEditingLine = canEditAllowed && editingLineKey === lineKey;
              return (
                <div key={l.pricing_line_code} className="flex justify-between text-xs text-ui-text2">
                  <span>{l.label}</span>
                  {isEditingLine ? (
                    <input
                      type="number"
                      autoFocus
                      value={lineDraft}
                      disabled={busy}
                      onChange={(e) => setLineDraft(e.target.value)}
                      onBlur={() => {
                        setEditingLineKey(null);
                        const parsed = parseFloat(lineDraft);
                        if (!Number.isNaN(parsed) && parsed !== l.presentation_amount) {
                          void saveLineAmount(opt.option_id, l.pricing_line_code, parsed);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') setEditingLineKey(null);
                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                      }}
                      className="w-20 rounded-md border border-accent bg-ui-input px-1 py-0.5 text-right text-xs text-ui-text focus:outline-none"
                    />
                  ) : (
                    <span
                      onClick={canEditAllowed ? () => { setLineDraft(String(l.presentation_amount)); setEditingLineKey(lineKey); } : undefined}
                      title={canEditAllowed ? t('workflow.proposalClickToEditAmount', 'Click to edit') : undefined}
                      className={canEditAllowed ? 'cursor-text rounded hover:bg-ui-hover' : undefined}
                    >
                      {fmtMoney(l.presentation_amount, currency)}
                    </span>
                  )}
                </div>
              );
            })}
            {opt.discount.amount > 0 && (
              <div className="flex justify-between text-xs text-danger-text"><span>{t('workflow.estimateDiscount', 'Discount')}</span><span>−{fmtMoney(opt.discount.amount, currency)}</span></div>
            )}
            {opt.tax.tax_amount > 0 && (
              <div className="flex justify-between text-xs text-ui-text2"><span>{t('workflow.estimateTax', 'Tax')}</span><span>{fmtMoney(opt.tax.tax_amount, currency)}</span></div>
            )}
          </div>
          {isDraftEditable && opt.option_role !== 'PRIMARY' && (
            <button
              type="button" disabled={busy}
              onClick={async () => {
                setError('');
                try { await setRecommended({ engagementId, option_id: opt.option_id }).unwrap(); }
                catch (e) { setError(apiErrorMessage(e, t, t('workflow.proposalRecommendFailed', 'Could not change the recommended option.'))); }
              }}
              className="mt-1.5 rounded-md border border-accent px-2 py-0.5 text-xs font-semibold text-accent-text hover:bg-ui-hover disabled:opacity-60"
            >
              {t('workflow.proposalMakeRecommended', 'Make recommended')}
            </button>
          )}
        </div>
      ))}
      {/* Options priced but deliberately not sent. Kept visible (and re-includable in one
          click) rather than hidden — they are snapshotted on the document either way, and a
          Sales Agent needs to see what they chose to leave out. */}
      {isDraftEditable && excluded.length > 0 && (
        <div className="rounded-lg border border-dashed border-ui-border px-2.5 py-2">
          <p className="text-micro font-bold uppercase tracking-[0.06em] text-ui-text3">
            {t('workflow.proposalOptionsNotIncluded', 'Not included on this proposal')}
          </p>
          <div className="mt-1.5 space-y-1">
            {excluded.map((opt) => (
              <label key={opt.option_id} className="flex cursor-pointer items-center gap-2 text-xs text-ui-text2">
                <input
                  type="checkbox"
                  checked={false}
                  disabled={busy}
                  onChange={() => void toggleIncluded(opt.option_id, true)}
                  className="flex-shrink-0 accent-accent-fill"
                />
                <span className="truncate">
                  {opt.display_label || opt.option_id} · v{opt.estimate_option_version}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* The mockup's own bottom line — the number that actually goes on the contract, rather
          than leaving the reader to work out which of the option cards above is the live one. */}
      {recommended && (
        <div className="flex items-baseline justify-between border-t-2 border-ui-text pt-2">
          <span className="text-xs font-semibold text-ui-text">
            {t('workflow.proposalTotalRecommended', 'Total — recommended option')}
          </span>
          <span className="text-base font-bold text-ui-text">{fmtMoney(recommended.total, currency)}</span>
        </div>
      )}
      {error && <p className="text-xs font-medium text-danger-text">{error}</p>}
    </div>
  );
}

function EditButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button" onClick={onClick}
      className="mb-1 rounded-md border border-accent px-2 py-0.5 text-xs font-semibold text-accent-text hover:bg-ui-hover"
    >
      {label}
    </button>
  );
}

// Renders the section-type-specific preview of `content` — every section type the backend
// actually populates gets a real, data-driven preview (never a placeholder), matching
// app.domain.proposals.pdf's own per-section-type dispatch. `onEdit` opens this section's real
// popup editor (see ProposalSectionEditors.tsx) for the section types where content is a genuine
// user selection — omitted (no button rendered) for purely computed/derived sections.
/** Prose a client reads, edited where it sits: double-click the paragraph and it becomes a
 *  textarea. A modal for one block of text made changing a sentence a four-step round trip
 *  (button, dialog, save, dismiss) and hid the surrounding sections while you did it.
 *
 *  Commits on blur, matching the estimate editor's own autosave rule; Escape abandons the edit.
 */
function InlineText({
  text, editable, saving, onSave,
}: { text: string; editable: boolean; saving: boolean; onSave: (next: string) => void }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text);

  if (!editable) {
    return text
      ? <p className="text-xs leading-relaxed text-ui-text2">{text}</p>
      : <p className="text-xs text-ui-text3">{t('workflow.proposalNoContentYet', 'Nothing to show yet.')}</p>;
  }

  if (editing) {
    return (
      <textarea
        autoFocus
        value={draft}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          setEditing(false);
          if (draft !== text) onSave(draft);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { setDraft(text); setEditing(false); }
        }}
        rows={Math.max(3, Math.ceil(draft.length / 90))}
        className="w-full resize-y rounded-md border border-accent bg-ui-card px-2 py-1.5 text-xs leading-relaxed"
      />
    );
  }

  return (
    <p
      onDoubleClick={() => { setDraft(text); setEditing(true); }}
      title={t('workflow.proposalDoubleClickToEdit', 'Double-click to edit')}
      className={`cursor-text rounded-md text-xs leading-relaxed hover:bg-ui-hover ${text ? 'text-ui-text2' : 'text-ui-text3'}`}
    >
      {text || t('workflow.proposalDoubleClickToEdit', 'Double-click to edit')}
    </p>
  );
}

/** A muted line for an included section that has nothing in it yet.
 *
 *  Nothing in this document may render as `null`. A section the template includes but whose
 *  content is empty used to disappear entirely, so the modal showed a heading with an invisible
 *  gap under it and the draft read as broken. An explicit empty state says which part of the
 *  document still needs filling. */
function EmptyLine({ children }: { children: React.ReactNode }) {
  return <p className="text-xs italic text-ui-text3">{children}</p>;
}

/** The letterhead: logo (or the company's initials on its own brand colour), name, address and
 *  contact line — the block at the top of the mockup's document. */
function CompanyLetterhead({ data }: { data: Record<string, unknown> }) {
  const { t } = useTranslation();
  const name = (data.company_name as string) || '';
  const logo = data.logo_light_url as string | undefined;
  const color = (data.primary_color as string) || DEFAULT_BRAND.primaryColor;
  const contactLine = [data.phone, data.email, data.license].filter(Boolean).join(' · ');
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

  if (!name && !logo) {
    return <EmptyLine>{t('workflow.proposalBrandingMissing', 'Add your company name, address and licence in Settings → Brand Settings.')}</EmptyLine>;
  }

  return (
    <div className="flex items-start gap-3 border-b-2 pb-3" style={{ borderColor: color }}>
      {logo
        ? <img src={logo} alt={name} className="h-10 w-10 flex-shrink-0 rounded-[10px] object-contain" />
        : (
          <span
            aria-hidden
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[10px] text-base font-bold text-white"
            style={{ backgroundColor: color }}
          >
            {initials || '—'}
          </span>
        )}
      <div className="min-w-0 flex-1">
        <p className="text-base font-bold tracking-tight text-ui-text">{name}</p>
        {(data.address_line as string) && (
          <p className="mt-0.5 text-xs leading-relaxed text-ui-text3">{data.address_line as string}</p>
        )}
        {contactLine && <p className="text-xs leading-relaxed text-ui-text3">{contactLine}</p>}
      </div>
    </div>
  );
}

/** One column of the mockup's "PREPARED FOR" / "PROPOSAL" header pair.
 *
 *  No heading of its own: the section row above every body already prints the section title, and
 *  repeating it here rendered "PREPARED FOR" twice, one line above the other. */
function LabelledBlock({ lines }: { lines: (string | null | undefined)[] }) {
  const shown = lines.filter(Boolean) as string[];
  if (shown.length === 0) return null;
  return (
    <div className="min-w-[170px] flex-1 space-y-0.5">
      {shown.map((line, i) => <p key={i} className="text-xs leading-relaxed text-ui-text2">{line}</p>)}
    </div>
  );
}

/** The sign-off block — the client cannot sign in this preview, so these are the ruled lines the
 *  printed/PDF document carries, shown so the draft matches what gets sent. */
function AcceptanceBlock({ text, preparedBy, companyName }: { text: string; preparedBy?: string; companyName?: string }) {
  const { t } = useTranslation();
  const line = (label: string, width?: string) => (
    <div className={width ?? 'min-w-[150px] flex-1'}>
      <div className="h-[22px] border-b border-ui-text3/60" />
      <p className="mt-1 text-xs text-ui-text3">{label}</p>
    </div>
  );
  return (
    <div>
      {text && <p className="text-xs leading-relaxed text-ui-text2">{text}</p>}
      <div className="mt-3 flex flex-wrap gap-4">
        {line(t('workflow.proposalClientSignature', 'Client signature'))}
        {line(t('workflow.proposalSignatureDate', 'Date'), 'w-[110px]')}
      </div>
      <div className="mt-3 flex flex-wrap gap-4">
        {line([companyName, preparedBy].filter(Boolean).join(' · ') || t('workflow.proposalCompanySignature', 'Company representative'))}
        {line(t('workflow.proposalSignatureDate', 'Date'), 'w-[110px]')}
      </div>
    </div>
  );
}

function SectionBody({
  section, currency, isDraftEditable, engagementId, onEdit, onSaveText, savingText, doc,
}: {
  section: ProposalSection; currency: string; isDraftEditable: boolean; engagementId: string;
  onEdit: () => void; onSaveText: (text: string) => void; savingText: boolean;
  /** Facts that live on the proposal rather than on any one section. */
  doc: {
    statusLabel: string; preparedBy?: string; companyName?: string;
    estimateOptions: EstimateOptionRef[];
    /** The property the PHOTOS picker reads its media library from. Absent means that picker
     *  cannot open, so the section must not offer the button — see below. */
    propertyId?: string;
  };
}) {
  const { t } = useTranslation();
  const data = section.content.structured_data as Record<string, unknown> | null;
  const text = section.content.generated_text;

  switch (section.section_type) {
    case 'PRICING_OPTIONS':
      return (
        <PricingOptionsBody
          section={section} currency={currency} isDraftEditable={isDraftEditable}
          engagementId={engagementId} allOptions={doc.estimateOptions}
        />
      );
    case 'COMPANY_BRANDING':
      return <CompanyLetterhead data={(data ?? {}) as Record<string, unknown>} />;
    case 'CUSTOMER_PROPERTY': {
      const d = data as { customer_name?: string; property_address?: string } | null;
      if (!d?.customer_name && !d?.property_address) {
        return <EmptyLine>{t('workflow.proposalNoCustomerYet', 'No customer or property is linked to this engagement yet.')}</EmptyLine>;
      }
      return (
        <LabelledBlock lines={[d.customer_name, d.property_address]} />
      );
    }
    case 'PROPOSAL_INFORMATION': {
      const d = data as { proposal_number?: string; proposal_date?: string; valid_until?: string } | null;
      if (!d) return null;
      return (
        <LabelledBlock
          lines={[
            [d.proposal_number, doc.statusLabel].filter(Boolean).join(' · '),
            [
              d.proposal_date && t('workflow.proposalIssued', 'Issued {{date}}', { date: d.proposal_date }),
              d.valid_until && t('workflow.proposalValidTo', 'valid to {{date}}', { date: d.valid_until }),
            ].filter(Boolean).join(' · '),
            doc.preparedBy && t('workflow.proposalPreparedByLine', 'Prepared by {{name}}', { name: doc.preparedBy }),
          ]}
        />
      );
    }
    case 'ACCEPTANCE':
      return <AcceptanceBlock text={text ?? ''} preparedBy={doc.preparedBy} companyName={doc.companyName} />;
    case 'TIMELINE': {
      const duration = (data as { estimated_duration?: string | null } | null)?.estimated_duration;
      if (!text && !duration && !(isDraftEditable && section.allow_user_edit)) {
        return <EmptyLine>{t('workflow.proposalNoTimelineYet', 'No timeline has been set for this job yet.')}</EmptyLine>;
      }
      return (
        <div>
          {duration && <p className="text-xs font-medium text-ui-text">{duration}</p>}
          <InlineText
            text={text ?? ''}
            editable={isDraftEditable && section.allow_user_edit}
            saving={savingText}
            onSave={onSaveText}
          />
        </div>
      );
    }
    case 'MATERIALS': {
      const items = (data as { material_items?: { name: string }[] } | null)?.material_items ?? [];
      if (items.length === 0) return <p className="text-xs text-ui-text3">{t('workflow.proposalNoContentYet', 'Nothing to show yet.')}</p>;
      return <ul className="list-inside list-disc space-y-0.5 text-xs text-ui-text2">{items.map((m, i) => <li key={i}>{m.name}</li>)}</ul>;
    }
    case 'PHOTOS': {
      const photos = (data as { photos?: { media_id: string; s3_url: string; caption?: string | null }[] } | null)?.photos ?? [];
      return (
        <div>
          {/* Gated on the property too: the picker it opens reads that property's media library
              and renders nothing without it, so offering the button on `isDraftEditable` alone
              made it a dead click — pressing it did nothing at all, with no explanation. */}
          {isDraftEditable && doc.propertyId && (
            <EditButton label={t('workflow.proposalSelectPhotos', 'Select photos')} onClick={onEdit} />
          )}
          {isDraftEditable && !doc.propertyId && (
            <EmptyLine>
              {t('workflow.proposalPhotosNoProperty', 'No property is linked to this engagement, so there is no photo library to choose from.')}
            </EmptyLine>
          )}
          {photos.length === 0 && <p className="text-xs text-ui-text3">{t('workflow.proposalNoPhotosSelected', 'No photos selected yet.')}</p>}
          {photos.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {photos.slice(0, 6).map((p) => (
                <img key={p.media_id} src={p.s3_url} alt={p.caption || ''} className="h-12 w-12 rounded-md object-cover" />
              ))}
              {photos.length > 6 && <span className="flex h-12 w-12 items-center justify-center rounded-md bg-ui-subtle text-xs text-ui-text3">+{photos.length - 6}</span>}
            </div>
          )}
        </div>
      );
    }
    case 'WARRANTY': {
      const warranties = (data as { warranties?: { title: string; duration: string; description?: string }[] } | null)?.warranties ?? [];
      return (
        <div>
          {isDraftEditable && <EditButton label={t('workflow.proposalEditWarranty', 'Edit warranty')} onClick={onEdit} />}
          {warranties.length === 0 && (
            <EmptyLine>{t('workflow.proposalNoWarrantyYet', 'No warranty terms — add them in Settings → Proposal Content.')}</EmptyLine>
          )}
          {warranties.length > 0 && (
            <div className="space-y-1">
              {warranties.map((w, i) => (
                <div key={i} className="text-xs text-ui-text2"><span className="font-medium text-ui-text">{w.title}</span> — {w.duration}</div>
              ))}
            </div>
          )}
        </div>
      );
    }
    case 'PREVIOUS_WORK_REFERENCES': {
      const refs = (data as { references?: { reference_id: string; title: string }[] } | null)?.references ?? [];
      return (
        <div>
          {isDraftEditable && <EditButton label={t('workflow.proposalSelectPreviousWork', 'Select previous work')} onClick={onEdit} />}
          {refs.length === 0 && <p className="text-xs text-ui-text3">{t('workflow.proposalNoReferencesYet', 'No references added yet.')}</p>}
          {refs.length > 0 && <ul className="list-inside list-disc space-y-0.5 text-xs text-ui-text2">{refs.map((r) => <li key={r.reference_id}>{r.title}</li>)}</ul>}
        </div>
      );
    }
    case 'PAYMENT_TERMS': {
      const schedule = (data as { payment_schedule?: { milestone: string; percentage: number }[] } | null)?.payment_schedule ?? [];
      return (
        <div>
          {isDraftEditable && <EditButton label={t('workflow.proposalChangePaymentTerm', 'Change payment terms')} onClick={onEdit} />}
          <div className="space-y-0.5">
            {!text && schedule.length === 0 && (
              <EmptyLine>{t('workflow.proposalNoPaymentTermsYet', 'No payment terms — add them in Settings → Proposal Content.')}</EmptyLine>
            )}
            {text && <p className="text-xs text-ui-text2">{text}</p>}
            {schedule.map((m, i) => <p key={i} className="text-xs text-ui-text3">{m.milestone} — {m.percentage}%</p>)}
          </div>
        </div>
      );
    }
    case 'EXCLUSIONS': {
      const items = (data as { items?: { exclusion_code: string; text: string }[] } | null)?.items ?? [];
      return (
        <div>
          {isDraftEditable && <EditButton label={t('workflow.proposalSelectExclusions', 'Select exclusions')} onClick={onEdit} />}
          {items.length === 0 && (
            <EmptyLine>{t('workflow.proposalNoExclusionsYet', 'No exclusions — add them in Settings → Proposal Content.')}</EmptyLine>
          )}
          {items.length > 0 && <ul className="list-inside list-disc space-y-0.5 text-xs text-ui-text2">{items.map((i) => <li key={i.exclusion_code}>{i.text}</li>)}</ul>}
        </div>
      );
    }
    case 'TERMS_CONDITIONS': {
      const clauses = (data as { clauses?: { clause_code: string; text: string }[] } | null)?.clauses ?? [];
      return (
        <div>
          {isDraftEditable && <EditButton label={t('workflow.proposalSelectClauses', 'Select terms & conditions')} onClick={onEdit} />}
          {clauses.length === 0 && (
            <EmptyLine>{t('workflow.proposalNoClausesYet', 'No terms & conditions — add them in Settings → Proposal Content.')}</EmptyLine>
          )}
          {clauses.length > 0 && <ul className="list-inside list-disc space-y-0.5 text-xs text-ui-text2">{clauses.map((c) => <li key={c.clause_code}>{c.text}</li>)}</ul>}
        </div>
      );
    }
    default:
      // COVER_LETTER / ASSESSMENT_SUMMARY / SCOPE_OF_WORK / FREE_FORM — prose the client reads,
      // so it is editable wherever the template allows it. Sections that only mirror data owned
      // elsewhere carry allow_user_edit=false and stay read-only.
      if (!text && !(isDraftEditable && section.allow_user_edit)) {
        return <EmptyLine>{t('workflow.proposalNoContentYet', 'Nothing to show yet.')}</EmptyLine>;
      }
      return (
        <InlineText
          text={text ?? ''}
          editable={isDraftEditable && section.allow_user_edit}
          saving={savingText}
          onSave={onSaveText}
        />
      );
  }
}

function EstimateOptionBreakdown({
  option, engagementId, estimateSetId,
}: {
  option: NonNullable<ReturnType<typeof useGetCurrentProposalQuery>['data']>['estimate_options'][number];
  engagementId: string;
  estimateSetId?: string;
}) {
  const { t } = useTranslation();
  const { data: estimateDetail } = useGetEstimateVersionQuery(
    { estimateCode: option.option_id, engagementId, versionNo: option.estimate_option_version ?? 0 },
    { skip: !option.option_id },
  );
  const cs = estimateDetail?.commercial_summary;
  return (
    <div className="rounded-[9px] border border-ui-border px-3 py-3">
      <p className="mb-2 text-xs font-bold text-ui-text">
        {estimateSetId ? `${estimateSetId} · ` : ''}
        {option.option_id}
        {option.estimate_option_version != null ? ` v${option.estimate_option_version}` : ''}
        {option.display_label ? ` · ${option.display_label}` : ''}
      </p>
      {cs ? (
        <div className="flex flex-col gap-1.5">
          <div className="flex justify-between text-xs">
            <span className="text-ui-text2">{t('workflow.estimateMaterialCost', 'Material cost')}</span>
            <b className="text-ui-text">{fmtMoney(cs.material_cost_total, cs.currency)}</b>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-ui-text2">{t('workflow.estimateServiceCost', 'Service cost')}</span>
            <b className="text-ui-text">{fmtMoney(cs.labour_cost_total + cs.equipment_cost_total + cs.subcontract_cost_total, cs.currency)}</b>
          </div>
          <div className="flex justify-between border-t border-ui-border pt-1.5 text-xs">
            <span className="text-ui-text2">{t('workflow.estimateBoqCost', 'BOQ cost')}</span>
            <b className="text-ui-text">{fmtMoney(cs.boq_cost_total, cs.currency)}</b>
          </div>
          {cs.margin?.amount != null && cs.margin.amount !== 0 && (
            <div className="flex justify-between text-xs">
              <span className="text-ui-text2">{t('workflow.estimateMargin', 'Margin')}</span>
              <b className="text-ui-text">{fmtMoney(cs.margin.amount, cs.currency)}</b>
            </div>
          )}
          {cs.discount?.amount != null && cs.discount.amount !== 0 && (
            <div className="flex justify-between text-xs">
              <span className="text-ui-text2">{t('workflow.estimateDiscount', 'Discount')}</span>
              <b className="text-danger-text">−{fmtMoney(cs.discount.amount, cs.currency)}</b>
            </div>
          )}
          <div className="flex justify-between border-t border-ui-border pt-1.5 text-sm">
            <b className="text-ui-text">{t('workflow.estimateTotal', 'Estimate total')}</b>
            <b className="text-ui-text">{fmtMoney(cs.grand_total, cs.currency)}</b>
          </div>
        </div>
      ) : (
        <p className="text-xs text-ui-text3">{t('workflow.proposalEstimateLoading', 'Loading…')}</p>
      )}
    </div>
  );
}

// ─── Compact "Proposal Prepared" card ────────────────────────────────────────
// Shown when the proposal is submitted for Owner review (status=DRAFT, is_editable=false).
// Matches the approved mockup: summary row + "Preview proposal" modal + "View estimate" inline.
function ProposalPreparedCard({
  proposal, engagementId, templateName, shownCount, onOpenAssessment, onViewEstimate, previewOpenSignal, interactSignal, onSurfaceContext,
}: {
  proposal: ProposalOut;
  engagementId: string;
  templateName: string;
  shownCount: number;
  onOpenAssessment?: () => void;
  onViewEstimate?: () => void;
  previewOpenSignal?: number;
  /** SAM's proposal_surface_interact tool, surface='preview_proposal' — scroll or close the
   *  already-open preview modal. */
  interactSignal?: ProposalSurfaceInteractPayload & { tick: number } | null;
  /** Reports whether the preview modal is open on every change, whoever caused it. */
  onSurfaceContext?: (state: { preview_open: boolean }) => void;
}) {
  const { t } = useTranslation();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [estViewOpen, setEstViewOpen] = useState(false);
  const previewScrollRef = useRef<HTMLDivElement | null>(null);

  // SAM's proposal_prepared_open tool with view="preview_proposal" bumps this signal.
  useEffect(() => { if (previewOpenSignal) setPreviewOpen(true); }, [previewOpenSignal]);
  useEffect(() => { onSurfaceContext?.({ preview_open: previewOpen }); }, [previewOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const pendingPreviewScrollRef = useRef<ProposalSurfaceInteractPayload | null>(null);
  const applyPreviewScroll = (p: ProposalSurfaceInteractPayload) => {
    const el = previewScrollRef.current;
    if (!el) return;
    const { action, section } = p;
    if (action === 'scroll_top') el.scrollTo({ top: 0, behavior: 'smooth' });
    else if (action === 'scroll_bottom') el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    else if (action === 'scroll_down') el.scrollBy({ top: el.clientHeight * 0.8, behavior: 'smooth' });
    else if (action === 'scroll_up') el.scrollBy({ top: -el.clientHeight * 0.8, behavior: 'smooth' });
    else if (action === 'scroll_to_section' && section) {
      const needle = section.trim().toLowerCase();
      const match = Array.from(el.querySelectorAll<HTMLElement>('[data-section-type]'))
        .find((n) => (n.dataset.sectionType || '').toLowerCase().includes(needle)
          || (n.textContent || '').toLowerCase().includes(needle));
      match?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  };
  // SAM's proposal_surface_interact tool — scroll/close the preview modal, opening it first if a
  // scroll/section request arrives while it's still closed (same "open + scroll" convenience the
  // estimate/assessment surfaces have).
  useEffect(() => {
    if (!interactSignal) return;
    if (interactSignal.action === 'close') {
      setPreviewOpen(false);
      return;
    }
    if (!previewOpen) {
      pendingPreviewScrollRef.current = interactSignal;
      setPreviewOpen(true);
      return;
    }
    applyPreviewScroll(interactSignal);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactSignal?.tick]);
  useEffect(() => {
    if (!previewOpen || !pendingPreviewScrollRef.current) return;
    const p = pendingPreviewScrollRef.current;
    pendingPreviewScrollRef.current = null;
    requestAnimationFrame(() => applyPreviewScroll(p));
  }, [previewOpen]);
  const [fetchPdf, { isFetching: downloadingPdf }] = useLazyGetProposalPdfQuery();
  const [pdfError, setPdfError] = useState('');

  // When a parent-level callback is wired, "View estimate" expands the EstimateSummaryDisplayCard
  // above rather than showing the local inline breakdown.
  const handleViewEstimate = onViewEstimate ?? (() => setEstViewOpen((v) => !v));
  const showLocalBreakdown = !onViewEstimate && estViewOpen;

  const includedOptions = proposal.estimate_options.filter((o) => o.included);
  const allOptions = includedOptions.length > 0 ? includedOptions : proposal.estimate_options.slice(0, 1);

  const optionNames = proposal.estimate_options
    .filter((o) => o.included)
    .map((o) => o.display_label)
    .filter(Boolean)
    .join(' + ');

  const downloadPdf = async () => {
    setPdfError('');
    try {
      const blob = await fetchPdf(engagementId).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${proposal.proposal_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setPdfError(t('workflow.proposalPdfFailed', 'Could not generate the PDF.'));
    }
  };

  // SAM's proposal_surface_interact tool, surface='preview_proposal', action='download' — same
  // "Download PDF" button, triggered without the user having to click it once the preview is
  // already open (e.g. "open this proposal" then "download this").
  useEffect(() => {
    if (interactSignal?.action === 'download') void downloadPdf();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactSignal?.tick]);

  const primaryOption = proposal.estimate_options[0];
  const serverSections = [...(proposal.sections ?? [])].sort((a, b) => a.display_order - b.display_order);
  const preparedByLine = [proposal.created_by_name || proposal.created_by, proposal.created_at
    ? new Date(proposal.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : null].filter(Boolean).join(' · ');

  return (
    <div className="space-y-0">
      {/* Summary card */}
      <div className="rounded-xl border border-ui-border px-3.5 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-micro font-bold uppercase tracking-[0.08em] text-ui-text3">
              {t('workflow.proposalLabel', 'Proposal')}
            </p>
            <p className="mt-1 truncate text-xs font-semibold text-ui-text">
              {proposal.proposal_number} · v{proposal.proposal_version}
              {proposal.estimate_set_id ? ` · from ${proposal.estimate_set_id}` : ''}
              {optionNames ? ` · ${optionNames}` : ''}
            </p>
            {preparedByLine && (
              <p className="mt-0.5 text-xs text-ui-text3">
                {t('workflow.proposalPreparedBy', 'Prepared by')} {preparedByLine}
              </p>
            )}
            <p className="mt-0.5 text-xs text-ui-text3">
              {templateName}
              {` · v${proposal.proposal_version}`}
              {` · ${shownCount} ${t('workflow.proposalSectionsLabel', 'sections')}`}
              {proposal.estimate_options.filter((o) => o.included).length > 0
                && ` · ${proposal.estimate_options.filter((o) => o.included).length} ${t('workflow.proposalOptionsLabel', 'options')}`}
            </p>
          </div>
          <div className="flex-shrink-0 text-right">
            <span className="mt-1 inline-block rounded-md bg-warning/15 px-2 py-0.5 text-xs font-bold text-warning-text">
              {t('workflow.proposalAwaitingReview', 'Awaiting review')}
            </span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            className="rounded-lg border border-accent bg-ui-card px-3 py-1.5 text-xs font-semibold text-accent-text hover:bg-accent-soft"
          >
            {t('workflow.proposalPreview', 'Preview proposal')}
          </button>
          <button
            type="button"
            onClick={handleViewEstimate}
            className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
              showLocalBreakdown
                ? 'border-accent/30 bg-accent-soft/20 text-accent-text'
                : 'border-ui-border bg-ui-card text-ui-text2 hover:bg-ui-hover'
            }`}
          >
            {t('workflow.proposalViewEstimate', 'View estimate')}
          </button>
          {onOpenAssessment && (
            <button
              type="button"
              onClick={onOpenAssessment}
              className="rounded-lg border border-ui-border bg-ui-card px-3 py-1.5 text-xs font-semibold text-ui-text2 hover:bg-ui-hover"
            >
              {t('workflow.openAssessmentCard', 'Open assessment')}
            </button>
          )}
        </div>

        {/* Inline estimate cost breakdown — shown only when no parent callback is wired */}
        {showLocalBreakdown && (
          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-ui-text3">
                {t('workflow.proposalEstimateBreakdown', 'Estimate breakdown')}
              </p>
              <button
                type="button"
                onClick={() => setEstViewOpen(false)}
                className="text-sm text-ui-text3 hover:text-ui-text"
              >
                ×
              </button>
            </div>
            {allOptions.map((opt) => (
              <EstimateOptionBreakdown
                key={opt.option_id}
                option={opt}
                engagementId={engagementId}
                estimateSetId={proposal.estimate_set_id ?? undefined}
              />
            ))}
            <p className="text-xs leading-relaxed text-ui-text3">
              {t('workflow.proposalEstimateLocked', 'Locked to the estimate version this proposal was built from — the estimate set froze on Prepare Proposal.')}
            </p>
          </div>
        )}
      </div>

      {/* Preview proposal modal — same as the existing full modal */}
      {previewOpen && (
        <div
          className="modal-scrim fixed inset-0 z-overlay flex items-start justify-center p-4"
          onClick={() => setPreviewOpen(false)}
        >
          <div
            className="my-6 flex max-h-[calc(100dvh-3rem)] w-full max-w-[760px] flex-col overflow-hidden rounded-2xl bg-ui-card shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-shrink-0 items-center justify-between gap-2.5 border-b border-ui-border px-5 py-3.5">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-ui-text">
                  {proposal.proposal_number} v{proposal.proposal_version} · {t('workflow.proposalPreviewLabel', 'preview')}
                </p>
                <p className="mt-0.5 truncate text-xs text-ui-text3">
                  {t('workflow.proposalViewOnly', 'View only · submitted for review')}
                </p>
              </div>
              <button
                type="button" disabled={downloadingPdf} onClick={() => void downloadPdf()}
                className="flex-shrink-0 rounded-lg border border-ui-border px-2.5 py-1.5 text-xs font-semibold text-ui-text2 hover:bg-ui-hover disabled:opacity-60"
              >
                {downloadingPdf ? t('workflow.proposalGeneratingPdf', 'Generating…') : t('workflow.proposalDownloadPdf', 'Download PDF')}
              </button>
              <button
                type="button" onClick={() => setPreviewOpen(false)}
                className="flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center rounded-md border border-ui-border text-sm text-ui-text2 hover:bg-ui-hover"
              >
                ×
              </button>
            </div>
            <div ref={previewScrollRef} className="min-h-0 flex-1 overflow-y-auto">
              <div className="border-b border-ui-border bg-ui-subtle px-3 py-2">
                <p className="truncate text-xs text-ui-text3">
                  {t('workflow.proposalFrom', 'From')} {primaryOption?.option_id} v{primaryOption?.estimate_option_version}
                </p>
                <p className="mt-0.5 truncate text-xs text-ui-text3">
                  {t('workflow.proposalPreparedBy', 'Prepared by')} {proposal.created_by_name || proposal.created_by} · {proposal.created_at && new Date(proposal.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                </p>
              </div>
              <div className="divide-y divide-ui-border">
                {serverSections.filter((s) => s.included).map((section) => (
                  <div key={section.proposal_section_id} data-section-type={section.section_type} className="px-3 py-2.5">
                    <p className="text-micro font-semibold uppercase tracking-wide text-ui-text3">
                      {section.section_title || humanize(section.section_type)}
                    </p>
                    <div className="mt-1">
                      <SectionBody
                        section={section} currency={proposal.currency} isDraftEditable={false} engagementId={engagementId}
                        doc={{
                          statusLabel: t('workflow.proposalStatusPrepared', 'Draft'),
                          preparedBy: proposal.created_by_name || undefined,
                          companyName: (serverSections.find((s) => s.section_type === 'COMPANY_BRANDING')?.content.structured_data as { company_name?: string } | null)?.company_name || undefined,
                          estimateOptions: proposal.estimate_options,
                        }}
                        onEdit={() => {}}
                        savingText={false}
                        onSaveText={() => {}}
                      />
                    </div>
                  </div>
                ))}
              </div>
              {pdfError && <p className="border-t border-ui-border px-3 py-2 text-xs font-medium text-danger-text">{pdfError}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function ProposalDisplayCard({ engagementId, propertyId, onOpenAssessment, onViewEstimate, previewOpenSignal, interactSignal, onSurfaceContext }: ProposalDisplayCardProps) {
  const { t } = useTranslation();
  const { data: proposal } = useGetCurrentProposalQuery(engagementId);
  const [expanded, setExpanded] = useState(false);
  // SAM's proposal_prepared_open tool (view="preview_proposal") bumps this signal. Only relevant
  // to the edit-in-place branch below — the DRAFT/!isDraftEditable branch returns
  // ProposalPreparedCard instead, which handles the same signal itself.
  useEffect(() => { if (previewOpenSignal) setExpanded(true); }, [previewOpenSignal]);
  // Report the modal's visible state back to SAM on every change, whoever caused it (SAM or a
  // manual click) — same as ProposalPreparedCard's own effect above. Without this, opening the
  // draft editor by hand (the "Open ›" button below) never reached active_proposal_surface, so a
  // question like "what am I looking at" right after a manual open answered from stale context.
  useEffect(() => { onSurfaceContext?.({ preview_open: expanded }); }, [expanded]); // eslint-disable-line react-hooks/exhaustive-deps
  const [fetchPdf, { isFetching: downloadingPdf }] = useLazyGetProposalPdfQuery();
  const [pdfError, setPdfError] = useState('');
  const [updateSection, { isLoading: savingSection }] = useUpdateProposalSectionMutation();
  const [toggleError, setToggleError] = useState('');
  const [editingSection, setEditingSection] = useState<ProposalSection | null>(null);
  const [editError, setEditError] = useState('');
  // Only to explain the absence of a proposal (below) — shares the estimate card's cache, so
  // this adds no extra request.
  const { data: engagementEstimates } = useListEstimatesForEngagementQuery(engagementId);
  const { data: templates } = useListProposalTemplatesQuery();
  const [changeTemplate, { isLoading: changingTemplate }] = useChangeProposalTemplateMutation();
  const [templateError, setTemplateError] = useState('');

  // Drag-reorder state. These MUST be declared here, above the `if (!proposal) return null`
  // guard below — they used to sit next to the reorder helpers further down, which made the
  // hook count change the moment the proposal query resolved (null -> document). React threw
  // "Rendered more hooks than during the previous render", the ErrorBoundary caught it, and the
  // whole engagement panel collapsed to "Something went wrong" — which is why the proposal draft
  // appeared to be missing. Anything stateful added to this component belongs above that guard.
  //
  // `localOrder` is the optimistic overlay a drag writes so the list moves under the cursor
  // instead of waiting for a round trip; server order is otherwise the truth.
  const [localOrder, setLocalOrder] = useState<string[] | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [reorderError, setReorderError] = useState('');
  // The section the drag has most recently been moved onto. `dragover` fires continuously while
  // the pointer sits still, and each move changes the list the NEXT event measures against — so
  // re-applying the same hover swapped the dragged section back and forth for as long as it hovered
  // ([A,B,C,D] dragging A over C alternated [B,C,A,D] / [B,A,C,D] forever), and `onDrop` persisted
  // whichever side of the flicker it happened to land on. Applying a given target at most once per
  // hover makes the move idempotent; moving onto a different section, or back onto an earlier one,
  // still re-applies because the target changed.
  const lastDragOverId = useRef<string | null>(null);

  const serverSections = [...(proposal?.sections ?? [])].sort((a, b) => a.display_order - b.display_order);
  // Reset the overlay whenever the server's own ordering changes, so a rejected save snaps back
  // rather than leaving the UI lying.
  const serverOrderKey = serverSections.map((sec) => sec.proposal_section_id).join('|');
  useEffect(() => { setLocalOrder(null); }, [serverOrderKey]);

  const saveSection = async (patch: { structured_data?: Record<string, unknown>; generated_text?: string }) => {
    if (!editingSection) return;
    setEditError('');
    try {
      await updateSection({ engagementId, proposalSectionId: editingSection.proposal_section_id, ...patch }).unwrap();
      setEditingSection(null);
    } catch (err) {
      setEditError(apiErrorMessage(err, t, t('workflow.proposalSectionSaveFailed', 'Could not save this section.')));
    }
  };

  // Inline edits have no modal to hold the "which section" state, so they name it directly.
  const saveSectionText = async (section: ProposalSection, generated_text: string) => {
    setEditError('');
    try {
      await updateSection({ engagementId, proposalSectionId: section.proposal_section_id, generated_text }).unwrap();
    } catch (err) {
      setEditError(apiErrorMessage(err, t, t('workflow.proposalSectionSaveFailed', 'Could not save this section.')));
    }
  };

  const toggleSectionIncluded = async (section: ProposalSection, included: boolean) => {
    setToggleError('');
    try {
      await updateSection({ engagementId, proposalSectionId: section.proposal_section_id, included }).unwrap();
    } catch (err) {
      setToggleError(apiErrorMessage(err, t, t('workflow.proposalSectionToggleFailed', 'Could not update this section.')));
    }
  };

  const downloadPdf = async () => {
    setPdfError('');
    try {
      const blob = await fetchPdf(engagementId).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${proposal?.proposal_number ?? 'proposal'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setPdfError(t('workflow.proposalPdfFailed', 'Could not generate the PDF.'));
    }
  };

  // SAM's proposal_surface_interact tool, surface='preview_proposal' — this branch (proposal_returned,
  // edit-in-place) never wired interactSignal at all, so 'close'/'download' silently did nothing
  // here even though the tool's own contract promises them for every surface.
  useEffect(() => {
    if (!interactSignal) return;
    if (interactSignal.action === 'close') setExpanded(false);
    else if (interactSignal.action === 'download') void downloadPdf();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactSignal?.tick]);

  // No proposal document. This card is only mounted where the workflow says a proposal belongs
  // (module_displays binds proposal_summary_panel at the drafting status and at every proposal
  // status), so reaching here means one is expected and is not there.
  //
  // This used to `return null`. The panel that diagnosed it was removed on request, on the
  // reasoning that SAM's narration above is the single place engagement state gets explained —
  // but that narration is built only from list_transitions() (status, gates, permissions) and
  // structurally cannot see whether a proposal DOCUMENT exists. The result was an engagement
  // sitting at "Proposal Prepared", with a Proposal Review approval raised against it, showing a
  // blank space while SAM said the proposal was ready for review.
  //
  // So this says what is actually true, and nothing more. It is one quiet line, not the old
  // diagnostic block: a proposal is priced FROM an estimate, so the absence of one is the whole
  // explanation in the case that actually occurs.
  if (!proposal) {
    // The draft is built from ANY estimate now, completed or not (see
    // EstimateService.get_draft_proposal_basis), so the only legitimate reason to have no
    // proposal is having no estimate at all. Options present with no proposal is a genuine
    // anomaly and says so.
    const hasAnyEstimate = (engagementEstimates ?? []).length > 0;
    return (
      <div className="rounded-xl border border-dashed border-ui-border bg-ui-subtle px-3 py-2.5">
        <p className="text-xs font-semibold text-ui-text">
          {t('workflow.proposalNoneTitle', 'No proposal yet')}
        </p>
        <p className="mt-0.5 text-xs leading-relaxed text-ui-text3">
          {hasAnyEstimate
            ? t(
                'workflow.proposalNoneDespiteEstimate',
                'This engagement has an estimate but no proposal document. Add or regenerate an estimate option and the draft is rebuilt automatically.',
              )
            : t(
                'workflow.proposalNoneNoEstimate',
                'A proposal is priced from an estimate. Generate an estimate for this engagement and the draft is created automatically.',
              )}
        </p>
      </div>
    );
  }

  const statusMeta = PROPOSAL_STATUS_META[proposal.status];
  const badgeLabel = statusMeta ? t(statusMeta.key, statusMeta.defaultLabel) : humanize(proposal.status);
  const badgeCls = statusMeta?.cls ?? 'bg-ui-hover text-ui-text2';
  const isDraftEditable = proposal.is_editable;
  const primaryOption = proposal.estimate_options[0];

  const sortedSections = localOrder
    ? localOrder
        .map((id) => serverSections.find((sec) => sec.proposal_section_id === id))
        .filter((sec): sec is ProposalSection => !!sec)
    : serverSections;

  const shownCount = sortedSections.filter((sec) => sec.included).length;
  // Read off the branding section rather than fetched separately — it is the same name the
  // document itself prints, so the signature block can never disagree with the letterhead.
  const companyName = (
    sortedSections.find((sec) => sec.section_type === 'COMPANY_BRANDING')
      ?.content.structured_data as { company_name?: string } | null
  )?.company_name ?? '';
  // The property whose media library the PHOTOS picker reads. Resolved ONCE so the button and the
  // modal it opens can never disagree — they used to be gated on different things, which is how
  // the button became a dead click.
  const photoPropertyId = propertyId || proposal.property_id || undefined;
  const templateName = templates?.find((tpl) => tpl.proposal_template_code === proposal.proposal_template_code)
    ?.proposal_template_name ?? proposal.proposal_template_code;

  /** Moves `draggedId` to sit where `targetId` currently is, in the list the user can see. */
  const reorder = (draggedId: string, targetId: string): string[] => {
    const ids = sortedSections.map((sec) => sec.proposal_section_id);
    const from = ids.indexOf(draggedId);
    const to = ids.indexOf(targetId);
    if (from === -1 || to === -1 || from === to) return ids;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    return ids;
  };

  // Autosaves on drop — there is no "save layout" button, matching every other control in this
  // card. Only the sections whose position actually MOVED are sent: a 16-section document would
  // otherwise fire 16 writes because one block was nudged one place.
  const persistOrder = async (ids: string[]) => {
    setReorderError('');
    const before = new Map(serverSections.map((sec, i) => [sec.proposal_section_id, i]));
    const moved = ids.filter((id, i) => before.get(id) !== i);
    try {
      for (const id of moved) {
        await updateSection({
          engagementId, proposalSectionId: id, display_order: (ids.indexOf(id) + 1) * 10,
        }).unwrap();
      }
    } catch (err) {
      setLocalOrder(null);  // server rejected it — show what is actually stored
      setReorderError(apiErrorMessage(err, t, t('workflow.proposalReorderFailed', 'Could not save the new order.')));
    }
  };

  const onChangeTemplate = async (code: string) => {
    if (!code || code === proposal.proposal_template_code) return;
    setTemplateError('');
    try {
      await changeTemplate({ engagementId, proposal_template_code: code }).unwrap();
    } catch (err) {
      setTemplateError(apiErrorMessage(err, t, t('workflow.proposalTemplateChangeFailed', 'Could not change the proposal template.')));
    }
  };

  // proposal_prepared stage: DRAFT submitted for Owner review — show compact read-only summary
  // matching the approved mockup (not the full edit interface, which is only for proposal_returned).
  if (proposal.status === 'DRAFT' && !isDraftEditable) {
    return (
      <ProposalPreparedCard
        proposal={proposal}
        engagementId={engagementId}
        templateName={templateName}
        shownCount={sortedSections.filter((sec) => sec.included).length}
        onOpenAssessment={onOpenAssessment}
        onViewEstimate={onViewEstimate}
        previewOpenSignal={previewOpenSignal}
        interactSignal={interactSignal}
        onSurfaceContext={onSurfaceContext}
      />
    );
  }

  return (
    <div className="space-y-2">
      {/* ONE row, not two. The identity (number, version, template) and the open-the-document
          entry point were separate blocks that both led with "Proposal draft" — the same heading
          twice, stacked, saying the same thing.

          This row stays in THIS component rather than in the estimate card, and that is a
          constraint rather than a preference: `estimate_summary_panel` is bound at only three
          statuses (assessment_completed, proposal_prepared, proposal_returned) while
          `proposal_summary_panel` is bound at seven. At proposal_approved, proposal_sent,
          proposal_accepted and rejected the estimate card does not render at all — so an
          open-the-proposal button living inside it would make the finished document unreachable
          at exactly the statuses where it is the deliverable. */}

      {/* The template picker is a real control, so it cannot live inside that button. It sits
          ABOVE the row rather than below it because the template is what the draft is built FROM:
          changing it rebuilds the sections the row then reports on ("15/16 sections"), so reading
          top-down now follows the order things actually happen. It only appears when switching
          templates is both allowed and meaningful (more than one exists). */}
      {templateError && <p className="px-3 text-xs font-medium text-danger-text">{templateError}</p>}

      <div className="rounded-xl border border-ui-border bg-ui-subtle overflow-hidden">
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-ui-hover"
        >
          <span className="min-w-0 flex-1">
            <span className="block truncate text-xs font-bold text-ui-text">
              {proposal.proposal_number} · v{proposal.proposal_version}
            </span>
            <span className="mt-0.5 block truncate text-xs text-ui-text3">
              {isDraftEditable
                ? t('workflow.proposalEditInPlace', 'Edit in place')
                : t('workflow.proposalViewOnlyShort', 'View only')}
              {' · '}
              {t('workflow.proposalSectionsShownShort', '{{shown}}/{{total}} sections', { shown: shownCount, total: sortedSections.length })}
            </span>
          </span>
          <span className={`flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${badgeCls}`}>{badgeLabel}</span>
          <span className="flex-shrink-0 text-xs font-semibold text-accent-text">
            {t('workflow.proposalOpen', 'Open')} ›
          </span>
        </button>
      </div>

      {expanded && (
      <div
        className="modal-scrim fixed inset-0 z-overlay flex items-start justify-center p-4"
        onClick={() => setExpanded(false)}
      >
        {/* Only the BODY scrolls. Putting overflow on the overlay too meant the wheel landed on
            the outer scroller, which had nothing to move — the same bug the estimate editor hit. */}
        <div
          className="my-6 flex max-h-[calc(100dvh-3rem)] w-full max-w-[760px] flex-col overflow-hidden rounded-2xl bg-ui-card shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-shrink-0 flex-col border-b border-ui-border px-5 py-3.5">
            <div className="flex items-center justify-between gap-2.5">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-ui-text">{t('workflow.proposalDraftTitle', 'Proposal draft')}</p>
                <p className="mt-0.5 truncate text-xs text-ui-text3">
                  {isDraftEditable
                    ? t('workflow.proposalEditInPlace', 'Edit in place')
                    : t('workflow.proposalViewOnly', 'View only · submitted for review')}
                  {' · '}
                  {t('workflow.proposalSectionsShown', '{{shown}} of {{total}} sections shown', { shown: shownCount, total: sortedSections.length })}
                </p>
              </div>
              <button
                type="button" disabled={downloadingPdf} onClick={() => void downloadPdf()}
                className="flex-shrink-0 rounded-lg border border-ui-border px-2.5 py-1.5 text-xs font-semibold text-ui-text2 hover:bg-ui-hover disabled:opacity-60"
              >
                {downloadingPdf ? t('workflow.proposalGeneratingPdf', 'Generating…') : t('workflow.proposalDownloadPdf', 'Download PDF')}
              </button>
              <button
                type="button" onClick={() => setExpanded(false)}
                aria-label={t('workflow.close', 'Close')}
                className="flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center rounded-md border border-ui-border px-1.5 text-sm text-ui-text2 hover:bg-ui-hover"
              >
                ×
              </button>
            </div>
            {isDraftEditable && (
              <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5" onClick={e => e.stopPropagation()}>
                {savingSection && (
                  <span className="flex-shrink-0 rounded-md bg-ui-subtle px-2.5 py-1 text-xs font-semibold text-ui-text2">
                    {t('workflow.proposalSaving', 'Saving…')}
                  </span>
                )}
                {(templates?.length ?? 0) > 1 && (<>
                  <label htmlFor="proposal-template" className="flex-shrink-0 text-xs text-ui-text3">
                    {t('workflow.proposalTemplate', 'Proposal template')}
                  </label>
                  <select
                    id="proposal-template"
                    value={proposal.proposal_template_code}
                    disabled={changingTemplate}
                    onChange={(e) => void onChangeTemplate(e.target.value)}
                    className="min-w-[120px] flex-1 rounded-[7px] border border-ui-border bg-ui-card px-2 py-1 text-xs font-semibold text-ui-text disabled:opacity-60"
                  >
                    {templates?.map((tpl) => (
                      <option key={tpl.proposal_template_code} value={tpl.proposal_template_code}>
                        {tpl.proposal_template_name}
                      </option>
                    ))}
                  </select>
                </>)}
              </div>
            )}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="border-b border-ui-border bg-ui-subtle px-3 py-2">
          <p className="truncate text-xs text-ui-text3">
            {t('workflow.proposalFrom', 'From')} {primaryOption?.option_id} v{primaryOption?.estimate_option_version}
          </p>
          <p className="mt-0.5 truncate text-xs text-ui-text3">
            {t('workflow.proposalPreparedBy', 'Prepared by')} {proposal.created_by_name || proposal.created_by} · {fmtDateTime(proposal.created_at)}
          </p>
        </div>

        {proposal.status === 'REVISION_REQUESTED' && proposal.returned_reason && (
          <div className="mx-3 mt-3 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 ">
            <p className="text-xs font-medium text-danger-text">
              {t('workflow.proposalReturnedBy', 'Returned by')} {proposal.returned_by_name || t('workflow.proposalReturnedByFallback', 'reviewer')}
              {proposal.returned_on && ` · ${fmtDateTime(proposal.returned_on)}`}
            </p>
            <p className="mt-0.5 text-xs text-danger-text/90">&ldquo;{proposal.returned_reason}&rdquo;</p>
          </div>
        )}

        {toggleError && <p className="border-t border-ui-border px-3 py-2 text-xs font-medium text-danger-text">{toggleError}</p>}
        {reorderError && <p className="border-t border-ui-border px-3 py-2 text-xs font-medium text-danger-text">{reorderError}</p>}

        <div className="divide-y divide-ui-border">
          {sortedSections.map((section) => (
            <div
              key={section.proposal_section_id}
              draggable={isDraftEditable}
              onDragStart={() => { setDraggingId(section.proposal_section_id); lastDragOverId.current = null; }}
              onDragOver={(e) => {
                if (!draggingId || draggingId === section.proposal_section_id) return;
                e.preventDefault();  // without this the drop never fires
                // Already moved onto this one — see lastDragOverId. Re-applying is what made the
                // list oscillate under a stationary pointer.
                if (lastDragOverId.current === section.proposal_section_id) return;
                lastDragOverId.current = section.proposal_section_id;
                setLocalOrder(reorder(draggingId, section.proposal_section_id));
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (localOrder) void persistOrder(localOrder);
                setDraggingId(null);
                lastDragOverId.current = null;
              }}
              onDragEnd={() => { setDraggingId(null); lastDragOverId.current = null; }}
              className={`px-3 py-2.5 ${!section.included ? 'opacity-50' : ''} ${
                draggingId === section.proposal_section_id ? 'opacity-40' : ''
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <p className="flex min-w-0 items-center gap-1.5 text-micro font-semibold uppercase tracking-wide text-ui-text3">
                  {/* The mockup's own grip glyph. Only while the draft is editable — a grip on a
                      submitted proposal would advertise a drag that is refused. */}
                  {isDraftEditable && (
                    <span aria-hidden className="cursor-grab tracking-[0.2em] text-ui-text3/60 active:cursor-grabbing">⠿</span>
                  )}
                  <span className="truncate">{section.section_title || humanize(section.section_type)}</span>
                </p>
                {isDraftEditable && (
                  section.required || !section.allow_user_remove ? (
                    <span className="flex-shrink-0 text-xs font-semibold text-ui-text3">
                      {t('workflow.proposalSectionRequired', 'Required')}
                    </span>
                  ) : section.included ? (
                    <button
                      type="button"
                      onClick={() => void toggleSectionIncluded(section, false)}
                      className="flex-shrink-0 rounded border border-ui-border px-1.5 py-0.5 text-xs font-semibold text-ui-text3 hover:bg-ui-hover"
                    >
                      {t('workflow.proposalHideSection', 'Hide')}
                    </button>
                  ) : (
                    <span className="flex flex-shrink-0 items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => void toggleSectionIncluded(section, true)}
                        className="rounded border border-accent px-1.5 py-0.5 text-xs font-semibold text-accent-text hover:bg-ui-hover"
                      >
                        {t('workflow.proposalShowSection', 'Show')}
                      </button>
                      <span className="text-xs font-semibold text-danger-text">{t('workflow.proposalSectionHidden', 'Hidden')}</span>
                    </span>
                  )
                )}
              </div>
              <div className="mt-1">
                <SectionBody
                  section={section} currency={proposal.currency} isDraftEditable={isDraftEditable} engagementId={engagementId}
                  doc={{
                    statusLabel: badgeLabel,
                    preparedBy: proposal.created_by_name || undefined,
                    companyName: companyName || undefined,
                    estimateOptions: proposal.estimate_options,
                    propertyId: photoPropertyId,
                  }}
                  onEdit={() => { setEditError(''); setEditingSection(section); }}
                  savingText={savingSection}
                  onSaveText={(generated_text) => void saveSectionText(section, generated_text)}
                />
              </div>
            </div>
          ))}
        </div>

        {pdfError && <p className="border-t border-ui-border px-3 py-2 text-xs font-medium text-danger-text">{pdfError}</p>}
          </div>

          <p className="flex-shrink-0 border-t border-ui-border px-5 pb-4 pt-3 text-xs leading-relaxed text-ui-text3">
            {t(
              'workflow.proposalDraftPreviewNote',
              'Preview of the draft as the client would see it. Content is not final until Prepare Proposal snapshots the selected option versions.',
            )}
          </p>
        </div>
      </div>
      )}

      {editingSection?.section_type === 'PHOTOS' && photoPropertyId && (
        <PhotoPickerModal
          propertyId={photoPropertyId} saving={savingSection}
          initialMediaIds={((editingSection.content.structured_data as { photos?: { media_id: string }[] } | null)?.photos ?? []).map((p) => p.media_id)}
          onClose={() => setEditingSection(null)}
          onSave={(photos: SelectedPhoto[]) => void saveSection({ structured_data: { photos } })}
        />
      )}
      {editingSection?.section_type === 'WARRANTY' && (
        <WarrantyEditorModal
          saving={savingSection}
          initial={(editingSection.content.structured_data as { warranties?: WarrantyEntry[] } | null)?.warranties ?? []}
          onClose={() => setEditingSection(null)}
          onSave={(warranties) => void saveSection({ structured_data: { warranties } })}
        />
      )}
      {editingSection?.section_type === 'PAYMENT_TERMS' && (
        <PaymentTermPickerModal
          saving={savingSection}
          initialCode={(editingSection.content.structured_data as { payment_term_code?: string | null } | null)?.payment_term_code ?? null}
          onClose={() => setEditingSection(null)}
          onSave={(term) => void saveSection({
            structured_data: { payment_term_code: term.payment_term_code, payment_schedule: term.payment_schedule },
            generated_text: term.description_text ?? '',
          })}
        />
      )}
      {editingSection?.section_type === 'EXCLUSIONS' && (
        <ExclusionsPickerModal
          saving={savingSection}
          initialCodes={((editingSection.content.structured_data as { items?: { exclusion_code: string }[] } | null)?.items ?? []).map((i) => i.exclusion_code)}
          onClose={() => setEditingSection(null)}
          onSave={(items) => void saveSection({ structured_data: { items: items.map((i) => ({ exclusion_code: i.exclusion_code, text: i.text })) } })}
        />
      )}
      {editingSection?.section_type === 'TERMS_CONDITIONS' && (
        <TermClausesPickerModal
          saving={savingSection}
          initialCodes={((editingSection.content.structured_data as { clauses?: { clause_code: string }[] } | null)?.clauses ?? []).map((c) => c.clause_code)}
          onClose={() => setEditingSection(null)}
          onSave={(clauses) => void saveSection({
            structured_data: { term_set_code: null, clauses: clauses.map((c) => ({ clause_code: c.clause_code, clause_version: c.clause_version, text: c.text })) },
          })}
        />
      )}
      {editingSection?.section_type === 'PREVIOUS_WORK_REFERENCES' && (
        <PreviousWorkPickerModal
          saving={savingSection}
          initialIds={((editingSection.content.structured_data as { references?: { reference_id: string }[] } | null)?.references ?? []).map((r) => r.reference_id)}
          onClose={() => setEditingSection(null)}
          onSave={(refs) => void saveSection({
            structured_data: { references: refs.map((r) => ({ reference_id: r.reference_id, title: r.title, description: r.description, photo_attachment_ids: r.photo_attachment_ids })) },
          })}
        />
      )}
      {editError && <p className="mt-1 text-xs font-medium text-danger-text">{editError}</p>}
    </div>
  );
}

export default ProposalDisplayCard;
